// Entity section for System Architecture - 195 lines ✅
import { useState } from 'react'
import { Database, ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { EntityGraph } from './EntityGraph'

interface Entity {
  name: string
  purpose: string
  attributes: string[]
  relationships: string[]
  category: 'primary' | 'secondary' | 'graph'
}

const entities: Entity[] = [
  {
    name: 'organizations',
    purpose: 'Multi-tenant root entity for organization isolation',
    attributes: ['id (uuid)', 'name (text)', 'settings (jsonb)', 'created_at (timestamptz)'],
    relationships: ['HAS_MANY → users', 'HAS_MANY → issues', 'HAS_MANY → knowledge_entries', 'HAS_MANY → customers'],
    category: 'primary'
  },
  {
    name: 'users',
    purpose: 'System users with role-based access control',
    attributes: ['id (uuid)', 'email (text)', 'full_name (text)', 'role (text)', 'organization_id (uuid)', 'preferences (jsonb)'],
    relationships: ['BELONGS_TO → organizations', 'CREATES → issues', 'CREATES → knowledge_entries', 'VALIDATES → knowledge_entries'],
    category: 'primary'
  },
  {
    name: 'issues',
    purpose: 'Customer support tickets for D365 F&O problems',
    attributes: ['id (uuid)', 'title (text)', 'description (text)', 'status (text)', 'priority (text)', 'd365_version (text)', 'd365_module (text)'],
    relationships: ['BELONGS_TO → organizations', 'CREATED_BY → users', 'HAS_MANY → conversation_messages', 'RESOLVED_BY → knowledge_entries'],
    category: 'primary'
  },
  {
    name: 'knowledge_entries',
    purpose: 'Validated solutions and best practices database',
    attributes: ['id (uuid)', 'title (text)', 'description (text)', 'content (text)', 'category (text)', 'd365_versions (text[])', 'description_embedding (vector)'],
    relationships: ['BELONGS_TO → organizations', 'CREATED_BY → users', 'VALIDATED_BY → users', 'RESOLVES → issues', 'HAS_MANY → resolution_methods'],
    category: 'primary'
  },
  {
    name: 'resolution_methods',
    purpose: 'Step-by-step resolution procedures',
    attributes: ['id (uuid)', 'title (text)', 'description (text)', 'steps (jsonb)', 'success_count (int)', 'failure_count (int)', 'risk_level (text)'],
    relationships: ['BELONGS_TO → organizations', 'BELONGS_TO → knowledge_entries', 'CREATED_BY → users'],
    category: 'primary'
  },
  {
    name: 'customers',
    purpose: 'End customer organizations',
    attributes: ['id (uuid)', 'name (text)', 'contact_info (jsonb)', 'subscription_level (text)'],
    relationships: ['BELONGS_TO → organizations', 'HAS_MANY → customer_documents', 'HAS_MANY → issues'],
    category: 'secondary'
  },
  {
    name: 'customer_documents',
    purpose: 'Uploaded files and documentation',
    attributes: ['id (uuid)', 'file_name (text)', 'storage_path (text)', 'file_type (text)', 'upload_date (timestamptz)'],
    relationships: ['BELONGS_TO → customers', 'HAS_MANY → document_chunks', 'LINKED_TO → issues'],
    category: 'secondary'
  },
  {
    name: 'conversation_sessions',
    purpose: 'Chat sessions for conversational issue recording',
    attributes: ['id (uuid)', 'session_type (text)', 'status (text)', 'metadata (jsonb)'],
    relationships: ['BELONGS_TO → organizations', 'BELONGS_TO → users', 'LINKED_TO → issues', 'HAS_MANY → conversation_messages'],
    category: 'secondary'
  },
  {
    name: 'expert_profiles',
    purpose: 'Internal subject matter experts',
    attributes: ['id (uuid)', 'user_id (uuid)', 'expertise_domains (text[])', 'd365_modules (text[])'],
    relationships: ['BELONGS_TO → users', 'RECEIVES → expert_assignments'],
    category: 'secondary'
  },
  {
    name: 'kg_node',
    purpose: 'Knowledge graph node representing any entity',
    attributes: ['id (uuid)', 'entity_type (text)', 'entity_id (uuid)', 'label (text)', 'properties (jsonb)', 'embedding (vector)'],
    relationships: ['REPRESENTS → (issues | knowledge_entries | resolution_methods)', 'CONNECTED_BY → kg_edge'],
    category: 'graph'
  },
  {
    name: 'kg_edge',
    purpose: 'Knowledge graph relationships between nodes',
    attributes: ['id (uuid)', 'source_node_id (uuid)', 'target_node_id (uuid)', 'relationship_type (text)', 'weight (float)'],
    relationships: ['CONNECTS → kg_node (source)', 'CONNECTS → kg_node (target)'],
    category: 'graph'
  }
]

function EntityCard({ entity }: { entity: Entity }) {
  const [isExpanded, setIsExpanded] = useState(false)

  const categoryColors = {
    primary: 'bg-kaycoo-teal text-white',
    secondary: 'bg-blue-500 text-white',
    graph: 'bg-purple-500 text-white'
  }

  return (
    <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-4">
      <div
        className="flex cursor-pointer items-start justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-kaycoo-teal-dark" />
            <h3 className="font-mono text-lg font-semibold text-kaycoo-teal-dark">{entity.name}</h3>
            <Badge className={categoryColors[entity.category]}>{entity.category}</Badge>
          </div>
          <p className="mt-1 text-sm text-kaycoo-charcoal-2">{entity.purpose}</p>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-kaycoo-charcoal-2" />
        ) : (
          <ChevronRight className="h-5 w-5 text-kaycoo-charcoal-2" />
        )}
      </div>

      {isExpanded && (
        <div className="mt-4 space-y-3 border-t border-kaycoo-teal pt-3">
          <div>
            <h4 className="mb-2 text-sm font-semibold text-kaycoo-teal-dark">Attributes</h4>
            <div className="space-y-1">
              {entity.attributes.map((attr, i) => (
                <div key={i} className="font-mono text-xs text-kaycoo-charcoal-2">
                  • {attr}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="mb-2 text-sm font-semibold text-kaycoo-teal-dark">Relationships</h4>
            <div className="space-y-1">
              {entity.relationships.map((rel, i) => (
                <div key={i} className="text-xs text-kaycoo-charcoal-2">
                  {rel}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function EntitySection() {
  const [filter, setFilter] = useState<'all' | 'primary' | 'secondary' | 'graph'>('all')

  const filteredEntities = filter === 'all'
    ? entities
    : entities.filter(e => e.category === filter)

  const stats = {
    total: entities.length,
    primary: entities.filter(e => e.category === 'primary').length,
    secondary: entities.filter(e => e.category === 'secondary').length,
    graph: entities.filter(e => e.category === 'graph').length
  }

  return (
    <div className="space-y-6">
      {/* Interactive Graph */}
      <EntityGraph />

      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
        <h2 className="mb-4 text-2xl font-bold text-kaycoo-teal-dark">Database Entities (Detailed View)</h2>
        <p className="mb-4 text-kaycoo-charcoal-2">
          Complete overview of all {stats.total} database tables and their relationships.
        </p>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`rounded px-3 py-1 text-sm font-medium ${
              filter === 'all'
                ? 'bg-kaycoo-teal-dark text-white'
                : 'bg-gray-100 text-kaycoo-charcoal-2 hover:bg-gray-200'
            }`}
          >
            All ({stats.total})
          </button>
          <button
            onClick={() => setFilter('primary')}
            className={`rounded px-3 py-1 text-sm font-medium ${
              filter === 'primary'
                ? 'bg-kaycoo-teal text-white'
                : 'bg-gray-100 text-kaycoo-charcoal-2 hover:bg-gray-200'
            }`}
          >
            Primary ({stats.primary})
          </button>
          <button
            onClick={() => setFilter('secondary')}
            className={`rounded px-3 py-1 text-sm font-medium ${
              filter === 'secondary'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-kaycoo-charcoal-2 hover:bg-gray-200'
            }`}
          >
            Secondary ({stats.secondary})
          </button>
          <button
            onClick={() => setFilter('graph')}
            className={`rounded px-3 py-1 text-sm font-medium ${
              filter === 'graph'
                ? 'bg-purple-500 text-white'
                : 'bg-gray-100 text-kaycoo-charcoal-2 hover:bg-gray-200'
            }`}
          >
            Knowledge Graph ({stats.graph})
          </button>
        </div>
      </div>

      {/* Entity Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {filteredEntities.map((entity) => (
          <EntityCard key={entity.name} entity={entity} />
        ))}
      </div>
    </div>
  )
}
